import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { auth, db } from "../firebase"; // Make sure to configure your firebase file
import {
  collection,
  query,
  where,
  onSnapshot,
  updateDoc,
  doc,
  deleteDoc,
} from "firebase/firestore";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  TextField,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Box,
  CircularProgress,
  IconButton,
  Container,
  AppBar,
  Card,
  CardMedia,
  Toolbar,
  Typography,
  List,
  ListItem,
  Chip,
} from "@mui/material";
import DatePicker from "react-date-picker";
import "react-date-picker/dist/DatePicker.css";
import logoSvg from "../imgs/logo_border.png";
import { Work as WorkIcon, Task as TaskIcon, Lock as LockIcon, LockOpen as LockOpenIcon, Star as StarIcon } from "@mui/icons-material";
import BottomNav from "./BottomNav";
import BoostProjectDialog from "./BoostProjectDialog";

const STATUS_LABELS = {
  abierto: { label: "Abierto", color: "success" },
  en_progreso: { label: "En progreso", color: "warning" },
  cerrado: { label: "Cerrado", color: "default" },
};

const skillsOptions = [
  "Diseño Gráfico",
  "Programación",
  "Marketing Digital",
  "Redacción de Contenidos",
  "Traducción",
  "Diseño Web",
];

const ProyectsComponent = () => {
  const [proyects, setProyects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [currentProyect, setCurrentProyect] = useState(null);
  const [editData, setEditData] = useState({
    title: "",
    description: "",
    price: "",
    skills: [],
    timeToDo: new Date(),
  });
  const [skills, setSkills] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [boostingProyect, setBoostingProyect] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [categories, setCategories] = useState([
    "Diseño Web",
    "Diseño UX/UI",
    "Diseño Grafico",
    "Programación",
    "Desarrollo de Apps",
    "Community Manager",
    "Marketing Digital",
    "Diseño Multimedia",
    "Edición de Videos",
    "Edición de Imágenes",
    "Redacción de Contenidos",
    "Traducción",
  ]);
  const navigate = useNavigate();

  const isLoggedIn = () => {
    return !!auth.currentUser;
  };

  const getToken = async () => {
    if (auth.currentUser) {
      return auth.currentUser.getIdToken();
    }
    return null;
  };

  useEffect(() => {
    const checkAuthAndFetchProyects = async () => {
      if (!isLoggedIn()) {
        navigate("/login");
        return;
      }

      try {
        const token = await getToken();

        if (!token) {
          console.error("No se pudo obtener el token de autenticación.");
          return;
        }

        const currentUser = auth.currentUser;
        if (!currentUser) {
          console.error("No hay un usuario autenticado.");
          return;
        }
        setLoading(true);
        const { uid } = currentUser;
        const q = query(collection(db, "projects"), where("uid", "==", uid));
        const unsubscribe = onSnapshot(q, (snapshot) => {
          const proyectsData = snapshot.docs.map((doc) => {
            const data = doc.data();
            return {
              ...data,
              id: doc.id,
              title: data.title,
              description: data.description,
              price: data.price,
              skills: data.skills, // Ensure skills is an array
              status: data.status || "abierto",
              featured: data.featured || false,
              featuredUntil: data.featuredUntil ? data.featuredUntil.toDate() : null,
              timeToDo: data.timeToDo.toDate(),
              createdAt: data.createdAt.toDate(),
            };
          });
          setProyects(proyectsData);
          setLoading(false);
        });
        return () => unsubscribe();
      } catch (error) {
        console.error("Error al obtener proyectos:", error);
        setLoading(false);
      }
    };
    checkAuthAndFetchProyects();
  }, [navigate]);

  const handleEdit = async () => {
    try {
      const proyectRef = doc(db, "projects", currentProyect.id);
      await updateDoc(proyectRef, editData);
      setIsDialogOpen(false);
      setEditData({
        title: "",
        description: "",
        price: "",
        skills: [],
        timeToDo: new Date(),
      });
    } catch (error) {
      console.error("Error al actualizar el proyecto:", error);
    }
  };

  const handleDelete = async (id) => {
    try {
      const proyectRef = doc(db, "projects", id);
      await deleteDoc(proyectRef);
    } catch (error) {
      console.error("Error al eliminar el proyecto:", error);
    }
  };

  const handleToggleStatus = async (proyect) => {
    const nextStatus = proyect.status === "cerrado" ? "abierto" : "cerrado";
    try {
      const proyectRef = doc(db, "projects", proyect.id);
      await updateDoc(proyectRef, { status: nextStatus });
    } catch (error) {
      console.error("Error al actualizar el estado del proyecto:", error);
    }
  };

  const openDialog = (proyect) => {
    setCurrentProyect(proyect);
    setEditData({
      title: proyect.title,
      description: proyect.description,
      price: proyect.price,
      skills: Array.isArray(proyect.skills) ? proyect.skills : [], // Ensure skills is an array
      timeToDo: proyect.timeToDo,
    });
    setIsDialogOpen(true);
  };

  const closeDialog = () => {
    setIsDialogOpen(false);
    setEditData({
      title: "",
      description: "",
      price: "",
      skills: [],
      timeToDo: new Date(),
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setEditData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleSkillsChange = (event) => {
    const {
      target: { value },
    } = event;
    setEditData((prevData) => ({
      ...prevData,
      skills: typeof value === "string" ? value.split(",") : value,
    }));
  };

  const handleDateChange = (date) => {
    setEditData((prevData) => ({
      ...prevData,
      timeToDo: date,
    }));
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleCategoryChange = (e) => {
    setSkills(e.target.value);
  };

  const filteredProyects = proyects.filter((proyect) => {
    return (
      proyect.title &&
      proyect.title.toLowerCase().includes(searchTerm.toLowerCase()) &&
      (skills ? proyect.skills.includes(skills) : true) // Corrected this line
    );
  });

  const isFeaturedActive = (proyect) =>
    proyect.featured && proyect.featuredUntil && proyect.featuredUntil > new Date();

  const formatDate = (timeToDo) => {
    if (timeToDo instanceof Date) {
      return timeToDo.toLocaleDateString();
    } else if (timeToDo && timeToDo.toDate instanceof Function) {
      return timeToDo.toDate().toLocaleDateString();
    }
    return "";
  };

  const styles = {
    img: {
      maxWidth: "150px",
      maxHeight: "40px",
      display: "block",
      margin: "20px auto",
    },
    slogan: {
      color: "text.secondary",
      fontSize: "14px",
      fontWeight: "bold",
    },
    iconContainer: {
      display: "flex",
      width: "100%",
      borderTop: (theme) => `1px solid ${theme.palette.divider}`,
      justifyContent: "space-around",
      color: "text.secondary",
    },
    buscar: {
      display: "flex",
      flexDirection: "row",
      justifyContent: "center",
      width: "100%",
    },
    title: {
      fontSize: "24px",
      color: "primary.main",
      marginBottom: "7px",
    },
    description: {
      fontSize: "18px",
      marginBottom: "7px",
    },
    timeToDo: {
      color: "success.main",
      fontSize: "15px",
      marginBottom: "7px",
      fontFamily: (theme) => theme.custom.fontMono,
    },
    skills: {
      marginBottom: "7px",
    },
    price: {
      color: "success.main",
      marginBottom: "7px",
      fontFamily: (theme) => theme.custom.fontMono,
    },
    created: {
      fontSize: "10px",
      marginBottom: "7px",
      color: "text.secondary",
    },
    media: {
      width: "190px",
      marginTop: "25px",
    },
  };

  return (
    <div>
      <AppBar
        position="fixed"
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "center",
          marginBottom: "10px",
        }}
      >
        <Toolbar
          sx={{ flexDirection: "column", width: "100%", alignItems: "center" }}
        >
          <CardMedia
            component="img"
            image={logoSvg} // Utilizar la variable que contiene la imagen SVG
            title="Skach"
            style={styles.media} // Especificar estilos si es necesario
          />
          <Box>
            <Typography variant="h6" sx={styles.slogan}>
              ¡Crea un proyecto o busca uno!
            </Typography>
          </Box>
          <Toolbar sx={styles.iconContainer}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <IconButton href="/proyectos" sx={{ color: "text.secondary" }}>
                <WorkIcon />
              </IconButton>
              <Typography variant="caption" fontSize={9} align="center">
                Mis Proyectos
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <IconButton href="/propuestas" sx={{ color: "text.secondary" }}>
                <TaskIcon />
              </IconButton>
              <Typography variant="caption" fontSize={9} align="center">
                Mis Propuestas
              </Typography>
            </Box>
          </Toolbar>
        </Toolbar>
      </AppBar>
      <Container sx={{ mt: 23, mb: 20 }}>
        <Box sx={styles.buscar}>
          <TextField
            label="Buscar por nombre de proyecto..."
            value={searchTerm}
            onChange={handleSearchChange}
            variant="outlined"
            sx={{ width: "70%" }}
          />
          <TextField
            select
            label="Habilidad"
            value={skills}
            onChange={handleCategoryChange}
            variant="outlined"
            sx={{ width: "28%", ml: 1 }}
          >
            <MenuItem value={null}>Habilidad</MenuItem>
            {categories.map((cat) => (
              <MenuItem key={cat} value={cat}>
                {cat}
              </MenuItem>
            ))}
          </TextField>
        </Box>
        {loading ? (
          <Box sx={{ textAlign: "center", color: "primary.main", marginTop: 2.5 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {filteredProyects.length > 0 ? (
              <List>
                {filteredProyects.map((proyect) => (
                  <ListItem key={proyect.id} sx={{ display: "block", px: 0 }}>
                    <Card
                      sx={{
                        p: 3,
                        mb: 2.5,
                        width: "100%",
                        ...(isFeaturedActive(proyect) && {
                          border: "1px solid",
                          borderColor: "#d4a017",
                        }),
                      }}
                    >
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1, flexWrap: "wrap" }}>
                        <Typography variant="h5" sx={styles.title}>
                          {proyect.title}
                        </Typography>
                        <Box sx={{ display: "flex", gap: 0.5 }}>
                          {isFeaturedActive(proyect) && (
                            <Chip
                              size="small"
                              icon={<StarIcon sx={{ color: "#d4a017 !important" }} />}
                              label="Destacado"
                              sx={{ borderColor: "#d4a017", color: "#d4a017" }}
                              variant="outlined"
                            />
                          )}
                          <Chip
                            size="small"
                            label={STATUS_LABELS[proyect.status]?.label || "Abierto"}
                            color={STATUS_LABELS[proyect.status]?.color || "success"}
                          />
                        </Box>
                      </Box>
                      <Typography variant="body1" sx={styles.description}>
                        {proyect.description}
                      </Typography>
                      <Typography variant="body2" sx={styles.price}>
                        Precio: USD$ {proyect.price}
                      </Typography>
                      <Typography variant="body2" sx={styles.skills}>
                        Habilidades: {proyect.skills}
                      </Typography>
                      <Typography variant="body2" sx={styles.timeToDo}>
                        Fecha de entrega: {formatDate(proyect.timeToDo)}
                      </Typography>
                      <Typography variant="body2" sx={styles.created}>
                        Creado el: {proyect.createdAt.toLocaleDateString()}
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          justifyContent: "space-around",
                          width: "100%",
                        }}
                      >
                        <Button
                          fullWidth
                          sx={{ marginRight: "15px", marginTop: "15px" }}
                          variant="contained"
                          color="primary"
                          onClick={() => openDialog(proyect)}
                        >
                          Editar
                        </Button>
                        <Button
                          fullWidth
                          sx={{ marginTop: "15px" }}
                          variant="contained"
                          color="secondary"
                          onClick={() => handleDelete(proyect.id)}
                        >
                          Eliminar
                        </Button>
                      </Box>
                      <Button
                        fullWidth
                        sx={{ mt: 1.5 }}
                        variant="outlined"
                        startIcon={proyect.status === "cerrado" ? <LockOpenIcon /> : <LockIcon />}
                        onClick={() => handleToggleStatus(proyect)}
                      >
                        {proyect.status === "cerrado" ? "Reabrir proyecto" : "Cerrar proyecto"}
                      </Button>
                      <Button
                        fullWidth
                        sx={{ mt: 1.5, borderColor: "#d4a017", color: "#d4a017" }}
                        variant="outlined"
                        startIcon={<StarIcon sx={{ color: "#d4a017" }} />}
                        onClick={() => setBoostingProyect(proyect)}
                      >
                        {isFeaturedActive(proyect)
                          ? `Destacado hasta ${proyect.featuredUntil.toLocaleDateString()} · Extender`
                          : "Destacar proyecto"}
                      </Button>
                    </Card>
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography>No se encontraron proyectos.</Typography>
            )}
          </>
        )}
      </Container>
      <BottomNav />
      <Dialog open={isDialogOpen} onClose={closeDialog}>
        <DialogTitle>Editar Proyecto</DialogTitle>
        <DialogContent>
          <TextField
            name="title"
            label="Título"
            variant="outlined"
            fullWidth
            margin="normal"
            value={editData.title}
            onChange={handleChange}
          />
          <TextField
            name="description"
            label="Descripción"
            variant="outlined"
            fullWidth
            margin="normal"
            value={editData.description}
            onChange={handleChange}
          />
          <TextField
            name="price"
            label="Precio (USD$)"
            variant="outlined"
            fullWidth
            margin="normal"
            value={editData.price}
            onChange={handleChange}
          />
          <FormControl fullWidth margin="normal" variant="outlined">
            <InputLabel>Habilidades</InputLabel>
            <Select
              multiple
              value={editData.skills}
              onChange={handleSkillsChange}
              input={<OutlinedInput label="Habilidades" />}
              renderValue={(selected) => selected.join(", ")}
            >
              {skillsOptions.map((skill) => (
                <MenuItem key={skill} value={skill}>
                  <Checkbox checked={editData.skills.indexOf(skill) > -1} />
                  <ListItemText primary={skill} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <DatePicker
            onChange={handleDateChange}
            value={editData.timeToDo}
            format="y-MM-dd"
            clearIcon={null}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} color="secondary">
            Cancelar
          </Button>
          <Button onClick={handleEdit} color="primary">
            Guardar
          </Button>
        </DialogActions>
      </Dialog>
      {boostingProyect && (
        <BoostProjectDialog
          open={!!boostingProyect}
          projectId={boostingProyect.id}
          onClose={() => setBoostingProyect(null)}
          onBoosted={() => setBoostingProyect(null)}
        />
      )}
    </div>
  );
};

export default ProyectsComponent;
